import { useLocation, useNavigate } from 'react-router-dom';
import { Group, UnstyledButton, Stack, Text } from '@mantine/core';
import {
  IconLayoutDashboard,
  IconUserCircle,
  IconSettings,
  IconDotsCircleHorizontal,
} from '@tabler/icons-react';
import { useAuth } from '@/hooks/useEnrichedAuth';

/**
 * MobileBottomTabs — bottom navigation bar for mobile (shown in the footer
 * slot on small screens). The last tab ("More") opens the full-menu drawer.
 *
 * "Home" points at the user's real landing page rather than `/`, because `/` is
 * only a role-based redirect — a Home tab pinned to `/` with `end: true` would
 * never match once the redirect resolved.
 */

interface MobileBottomTabsProps {
  onMore: () => void;
}

export default function MobileBottomTabs({ onMore }: MobileBottomTabsProps) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { hasAnyRole } = useAuth();

  // Must match `getAuthedRedirectPath()` in `src/App.tsx`, or the Home tab never reads as active.
  const homePath = hasAnyRole(['ActsCareFacilitator', 'ActsCareLeader'])
    ? '/Dashboard'
    : '/Counselling/Overview';

  const tabs = [
    { label: 'Home', to: homePath, icon: IconLayoutDashboard, end: true },
    { label: 'Profile', to: '/profile', icon: IconUserCircle },
    { label: 'Settings', to: '/settings', icon: IconSettings },
  ];

  const isActive = (to: string, end = false) =>
    end ? pathname === to : pathname === to || pathname.startsWith(`${to}/`);

  const activeColor = 'var(--mantine-primary-color-filled)';
  const dimColor = 'var(--mantine-color-dimmed)';

  return (
    <Group h="100%" gap={0} grow wrap="nowrap" px={4}>
      {tabs.map((t) => {
        const Icon = t.icon;
        const active = isActive(t.to, t.end);
        return (
          <UnstyledButton
            key={t.to}
            onClick={() => navigate(t.to)}
            aria-label={t.label}
            style={{ height: '100%' }}
          >
            <Stack gap={2} align="center" justify="center" h="100%">
              <Icon size={22} stroke={1.6} color={active ? activeColor : dimColor} />
              <Text size="0.65rem" c={active ? undefined : 'dimmed'} fw={active ? 600 : 400}>
                {t.label}
              </Text>
            </Stack>
          </UnstyledButton>
        );
      })}

      <UnstyledButton onClick={onMore} aria-label="More menu" style={{ height: '100%' }}>
        <Stack gap={2} align="center" justify="center" h="100%">
          <IconDotsCircleHorizontal size={22} stroke={1.6} color={dimColor} />
          <Text size="0.65rem" c="dimmed">
            More
          </Text>
        </Stack>
      </UnstyledButton>
    </Group>
  );
}
